import * as Sentry from '@sentry/vue';
import type { Router } from 'vue-router';
import config from '@/config/widgets';

// We only reload once per target hash. If we reload to pick up a new hash but
// come back still running the old build (e.g. the new assets aren't being
// served yet due to CDN/cache lag or a partial deploy), reloading again would
// achieve nothing and loop forever.
const ATTEMPTED_RELOAD_HASH_KEY = 'attemptedReloadHash';
const REPORTED_RELOAD_HASH_KEY = 'reportedReloadHash';

// If there's a new build, load it in place of the next navigation, except to
// routes with unsafeRefresh set, which hold state a reload would lose (e.g.
// during signup/login).
//
// The check runs between navigations rather than during one: a reload during a
// navigation aborts the lazy route chunks it is loading, and every browser
// reports an aborted chunk as a load failure.
export function reloadOnNewVersion(router: Router) {
  let pendingReloadHash: string | null = null;
  let latestCheck = Promise.resolve();

  function checkForNewVersion() {
    latestCheck = fetch('/hash.json')
      .then((res) => {
        // The manifest is served uncached, so it is the one asset that can 5xx
        // at the edge while the rest of the app is fine. A failed check means
        // "no update known" and the next check tries again.
        if (!res.ok) return null;
        return res.json() as Promise<{ hash: string }>;
      })
      .then((res) => {
        if (!res) return;

        if (res.hash === config.buildSHA) {
          pendingReloadHash = null;
          sessionStorage.removeItem(ATTEMPTED_RELOAD_HASH_KEY);
          sessionStorage.removeItem(REPORTED_RELOAD_HASH_KEY);
          return;
        }

        if (res.hash === sessionStorage.getItem(ATTEMPTED_RELOAD_HASH_KEY)) {
          pendingReloadHash = null;
          if (sessionStorage.getItem(REPORTED_RELOAD_HASH_KEY) !== res.hash) {
            sessionStorage.setItem(REPORTED_RELOAD_HASH_KEY, res.hash);
            Sentry.captureMessage('new hash reload did not take effect', {
              level: 'warning',
              extra: { advertisedHash: res.hash, runningHash: config.buildSHA },
            });
          }
          return;
        }

        pendingReloadHash = res.hash;
      })
      .catch((e: unknown) => {
        // fetch rejects with a TypeError when the connection drops or the body
        // stream fails. Same story as a non-ok response, so not worth reporting.
        if (e instanceof TypeError) return;
        logError(e);
      });
  }

  router.beforeEach((to) => {
    if (pendingReloadHash === null || to.matched.some((record) => record.meta.unsafeRefresh)) {
      return true;
    }
    Sentry.captureMessage('new hash reload');
    sessionStorage.setItem(ATTEMPTED_RELOAD_HASH_KEY, pendingReloadHash);
    // oxlint-disable-next-line no-console
    console.log('New version detected, refreshing the page...');
    window.location.href = to.fullPath;
    return false;
  });

  router.afterEach(() => checkForNewVersion());

  // A tab left open across a deploy, such as one parked on the login page, would
  // otherwise only learn of the new build from the navigation after its next one.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForNewVersion();
  });

  return {
    /** Settles once the guard has acted on the most recent check. */
    latestCheck: () => latestCheck,
  };
}
